import {
  Activity,
  ArrowRight,
  BarChart3,
  Calendar,
  FileText,
  Package,
  TrendingUp,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/auth/AuthProvider';
import { Card } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';

const TILES = [
  {
    to: '/reports/analytics',
    icon: Activity,
    title: 'Deep analytics',
    description:
      'Year-over-year, P&L, payment mix, cohorts, aging, leaderboard, heatmap — everything in one place.',
    allow: ['owner', 'accountant'] as const,
    featured: true,
  },
  {
    to: '/reports/revenue',
    icon: TrendingUp,
    title: 'Revenue analytics',
    description: 'Daily / weekly / monthly trends with peaks and top products.',
    allow: ['owner', 'manager', 'accountant'] as const,
  },
  {
    to: '/reports/gst',
    icon: FileText,
    title: 'GST summary',
    description: 'CGST / SGST / IGST per slab — ready for filing.',
    allow: ['owner', 'accountant'] as const,
  },
  {
    to: '/reports/inventory',
    icon: Package,
    title: 'Inventory value',
    description: 'Cost-side total of every SKU on every shelf.',
    allow: ['owner', 'manager', 'accountant'] as const,
  },
  {
    to: '/reports/day-end',
    icon: Calendar,
    title: 'Day-end closing',
    description: 'Today\'s till + outstanding + low stock — one printable page.',
    allow: ['owner', 'manager', 'accountant'] as const,
  },
];

/** Hub page that just routes — single source of truth for every report
 *  the panel surfaces. CA-only and owner-only tiles are filtered by role. */
export function ReportsHubPage() {
  const { hasRole } = useAuth();
  const visible = TILES.filter((t) => t.allow.some((r) => hasRole(r)));

  return (
    <>
      <PageHeader
        title="Reports"
        description="Numbers your CA needs every month, plus the ones you'll want every Monday."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((t) => {
          const Icon = t.icon;
          return (
            <Link key={t.to} to={t.to} className="group">
              <Card className="hover:border-brand-500 transition-colors h-full">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-brand-500/10 text-brand-700 grid place-items-center flex-shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-ink">{t.title}</h3>
                    <p className="text-sm text-ink-soft mt-1">{t.description}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-ink-soft group-hover:text-brand-700 group-hover:translate-x-0.5 transition-all" />
                </div>
              </Card>
            </Link>
          );
        })}
        {visible.length === 0 && (
          <Card className="md:col-span-2 lg:col-span-3">
            <div className="text-center py-8">
              <BarChart3 className="h-10 w-10 text-ink-soft mx-auto mb-2" />
              <p className="text-sm text-ink-soft">
                Your role doesn't have access to any reports yet.
              </p>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
